import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatDialog, MatDialogRef, MatSnackBar } from "@angular/material";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  NavCategoryId,
  NavItemPrimaryInterface,
  NavItemSecondaryInterface,
} from "app/app-layout/models";
import { NavItemsService } from "app/app-layout/services/nav-items.service";
import { SurveyService } from "app/assessment/survey/services/survey.service";
import { AppScopes } from "app/core/authentication/models/scopes.enum";
import { AuthService } from "app/core/authentication/services/auth.service";
import { BrowserService } from "app/core/browser/services/browser.service";
import { RouterStateUrlInterface } from "app/core/browser/state/models/router-state-url.interface";
import { UserInterface } from "app/core/user/models";
import { NotificationService } from "app/notifications/services/notification.service";
import { environment } from "environments/environment";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";

import { ExtendLogoutDialogComponent } from "../extend-logout-dialog/extend-logout-dialog.component";

@Component({
  selector: "cz-app-layout",
  templateUrl: "./app-layout.component.html",
  styleUrls: ["./app-layout.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  public readonly primaryNavItems$: BehaviorSubject<NavItemPrimaryInterface[]>;
  public readonly secondaryNavItems$: BehaviorSubject<
    NavItemSecondaryInterface[]
  >;
  public readonly currentCategoryId$: BehaviorSubject<NavCategoryId>;
  public readonly loggedInUser$: BehaviorSubject<UserInterface>;
  public readonly languageSet$: BehaviorSubject<boolean>;
  public surveyProgress$: Observable<number>;
  public isDefaultUser: boolean;
  public isAdminsite: boolean;
  public _interval: any;
  public _sessionTimeOut: number;
  public currentdateTime: any;
  public activesessiontime: any;
  public hasPopupDisplayed: boolean = false;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public readonly dialog: MatDialog,
    private readonly _authService: AuthService,
    private readonly _browserService: BrowserService,
    private readonly _navItemsService: NavItemsService,
    private readonly _surveyService: SurveyService,
    private readonly _cookieService: CookieService,
    private readonly _translateService: TranslateService,
    private readonly _notificationService: NotificationService,
    private readonly router: Router,
    private readonly _snackBar: MatSnackBar,
  ) {
    this.primaryNavItems$ = new BehaviorSubject<NavItemPrimaryInterface[]>(
      undefined,
    );
    this.secondaryNavItems$ = new BehaviorSubject<NavItemSecondaryInterface[]>(
      undefined,
    );
    this.currentCategoryId$ = new BehaviorSubject<NavCategoryId>(undefined);
    this.loggedInUser$ = new BehaviorSubject<UserInterface>(undefined);
    this.languageSet$ = new BehaviorSubject<boolean>(undefined);
    // Admin Site Access menu based on Role or Workflow
    this._authService
      .isLoggedInUserAuthorized([
        AppScopes.ROLE_PERMISSION,
        AppScopes.ERGO_WORKFLOW,
      ])
      .pipe(take(1))
      .subscribe((adminSite: boolean) => {
        this.isAdminsite = adminSite;
      });
  }

  public StartInterval(): void {
    this._interval = setInterval(() => {
      this.currentdateTime = new Date();
      this.activesessiontime = new Date(
        localStorage.getItem("activeSessionTime"),
      );
      const diff: number = Math.round(
        Math.abs(this.currentdateTime - this.activesessiontime) / 1000,
      );
      if (
        diff >= this._sessionTimeOut - environment.AlertBeforeTimeOutSeconds &&
        localStorage.getItem("isExtendedPopupVisible") !== "yes"
      ) {
        this.openExtendLogoutDialog();
      }
      if (this._sessionTimeOut <= diff) {
        clearInterval(this._interval);
        this._authService.signOut(false, "api/logout?userLogout=").subscribe(
          (res: any) => {
            location.href = "/login";
          },
          (error: any) => {},
        );
      }
    }, 1000);
  }
  @HostListener("document:click", ["$event"])
  public documentClick(event: MouseEvent): void {
    this.saveActiveSessionCurrent();
  }
  public ngOnInit(): void {
    if (sessionStorage.getItem("isUserLoggedIn") !== "1") {
      this._cookieService.deleteAll("/");
      this.router.navigateByUrl("/logout");
    }
    this.saveActiveSessionCurrent();
    const cultureCookie: string = this._cookieService.get("Culture");
    this._translateService.setDefaultLang(cultureCookie);
    this._translateService
      .use(cultureCookie)
      .pipe(take(1))
      .subscribe(() => {
        this.languageSet$.next(true);
      });
    // Get the logged in user from the token
    this._authService
      .getLoggedInUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInterface) => {
        this._authService.getLoginUserData(user.id).subscribe(
          (res: any) => {
            if (res) {
              if (!res.tcAccept || res.forcePassword) {
                this.router.navigateByUrl("/logout");
              }
            }
          },
          (error) => {},
        );
        // debugger
        if (user.sessionTimeOut > 0) {
          this._sessionTimeOut = user.sessionTimeOut * 60;
          this.StartInterval();
        }
        this.loggedInUser$.next(user);
      });

    this._authService
      .isLoggedInUserAuthorized([AppScopes.DEFAULT_USER])
      .pipe(take(1))
      .subscribe(
        (isDefaultUser: boolean) => (this.isDefaultUser = isDefaultUser),
      );

    // Listen for changes to router state and update.
    this._browserService
      .selectRouterState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((update: RouterStateUrlInterface) => {
        if (!update) {
          return;
        }

        const route: string = this._browserService.determinePrimaryNavAppRoute(
          update.url,
        );
        this.updateCurrentCategoryId(this.primaryNavItems$.getValue(), route);
      });

    // Listen for changes to primary navigation items to trigger render.
    this._navItemsService
      .getPrimaryNavItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe((primaryNavItems: NavItemPrimaryInterface[]) => {
        const route: string = this._browserService.determinePrimaryNavAppRoute(
          this._browserService.getPath(),
        );
        this.updateCurrentCategoryId(primaryNavItems, route);
        this.primaryNavItems$.next(primaryNavItems);
      });

    this._notificationService
      .sendNotificationResults()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sendResult: number) => {
        if (sendResult && sendResult > 0) {
          // Static text to Language Variable updated
          this._snackBar.open(
            sendResult +
              this._translateService.instant("$message_notification_sent"),
            undefined,
            {
              duration: 3500,
            },
          );
        }
      });

    // Initialize secondary navigation items — reacts to both store and category changes
    this.initSecondaryNavItems();

    this.surveyProgress$ = this._surveyService.getSurveyProgress();
  }

  public ngOnDestroy(): void {
    if (this._interval) {
      clearInterval(this._interval);
    }
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openExtendLogoutDialog(): void {
    const dialogRef: MatDialogRef<ExtendLogoutDialogComponent> =
      this.dialog.open(ExtendLogoutDialogComponent, {
        height: "500px",
        width: "700px",
        data: {},
      });
    localStorage.setItem("isExtendedPopupVisible", "yes");
    /*dialogRef.afterClosed().subscribe((languageId: any) => {
      this.oldDefaultLanguage = this.defaultLanguage;
      if(languageId) {
        this.showSnackBar(this._translateService.instant('$admin_languages_confirm_update'));
      }
      this.backTranslateLanguageId = (languageId !== '') ? languageId : this.backTranslateLanguageId;
    });*/
  }
  public saveActiveSessionCurrent(): void {
    localStorage.setItem("activeSessionTime", "" + new Date());
    localStorage.setItem("isExtendedPopupVisible", "no");
  }

  /**
   * Helper function to determine what NavCategoryId is currently active for scoping secondary nav items.
   *
   * @param primaryNavItems Primary navigation items
   * @param currentRoute Currently active route
   */
  private updateCurrentCategoryId(
    primaryNavItems: Readonly<NavItemPrimaryInterface[]>,
    currentRoute: string,
  ): void {
    if (!primaryNavItems) {
      return;
    }
    const navItemMatch: NavItemPrimaryInterface = primaryNavItems.find(
      (item: NavItemPrimaryInterface) => item.route === currentRoute,
    );

    if (!navItemMatch) {
      return;
    }

    const categoryId: NavCategoryId = navItemMatch.categoryId;
    this.currentCategoryId$.next(categoryId);
  }

  /**
   * Set up a single reactive subscription that updates secondary nav items
   * whenever the store data or the active category changes.
   */
  private initSecondaryNavItems(): void {
    combineLatest(
      this._navItemsService.getSecondaryNavItems(),
      this.currentCategoryId$,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.onSecondaryNavItemsChanged(result[0], result[1]);
      });
  }

  private onSecondaryNavItemsChanged(
    secondaryNavItems: { [categoryId: string]: NavItemSecondaryInterface[] },
    categoryId: NavCategoryId,
  ): void {
    if (!secondaryNavItems || !categoryId) {
      return;
    }
    this.secondaryNavItems$.next(secondaryNavItems[categoryId]);
  }
}
